import React, { useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { FaPlus } from "react-icons/fa";
import {
  FiEdit2,
  FiTrash2,
  FiFileText,
  FiRefreshCw,
  FiExternalLink,
} from "react-icons/fi";
import PageLoader from "components/ui/PageLoader";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import SearchSelect from "components/ui/SearchSelect";
import Table from "components/ui/Table";
import Tooltip from "components/ui/Tooltip";
import CircularProgress from "components/ui/CircularProgress";
import { formatDate, formatDateTime } from "utils/helperFunctions";
import {
  useGetLegislationQuery,
  useGetLawQuery,
  useAddLegislationMutation,
  useUpdateLawMutation,
  useReadLawAgainMutation,
  useDeleteLawMutation,
  useSearchLegislationMutation,
} from "apis/legislation";
import { useGetJurisdictionsQuery } from "apis/division";

const ACCEPT = ".pdf,.docx,.txt,.md,.html,.htm,.zip";

const INSTRUMENT_LABEL = {
  act: "Act",
  regulation: "Regulation",
  guidance: "Guidance",
};
const INSTRUMENT_OPTIONS = [
  { value: "", label: "Not set" },
  ...Object.entries(INSTRUMENT_LABEL).map(([value, label]) => ({
    value,
    label,
  })),
];

const STATUS_STYLE = {
  processing: "bg-yellow-100 text-yellow-800",
  ready: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
};
const STATUS_LABEL = {
  processing: "Reading…",
  ready: "Ready",
  failed: "Failed",
};

const STANDING_STYLE = {
  upcoming: "bg-blue-100 text-blue-800",
  inForce: "bg-green-100 text-green-800",
  replaced: "bg-lightgray text-darkgray",
};
const STANDING_LABEL = {
  upcoming: "Upcoming",
  inForce: "In force",
  replaced: "Replaced",
};

const inputClass = (error) =>
  `form-input border ${error ? "border-red-500" : "border-mediumgray"}`;

const ActionIcon = ({ label, disabled, onClick, children }) => (
  <Tooltip
    tooltip={label}
    text={
      <button
        className={`view-icon ${disabled ? "pointer-events-none opacity-40" : ""}`}
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
      >
        {children}
      </button>
    }
  />
);

const sizeOf = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// A stored date as the day it is where the law applies ("2026-11-17"), for a date field.
const dayInput = (date, timezone) =>
  date
    ? new Intl.DateTimeFormat("en-CA", { timeZone: timezone || "UTC" }).format(
        new Date(date),
      )
    : "";

const jurisdictionOption = (jurisdiction) => ({
  value: jurisdiction.code,
  label: `${jurisdiction.name} (${jurisdiction.code})`,
});

// ─── Add ──────────────────────────────────────────────────────────────────────
// Mounted only while open, so each time it starts empty.
const AddModal = ({ onClose }) => {
  const [progress, setProgress] = useState(null);
  const add = useAddLegislationMutation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { file: null } });

  const submitForm = ({ file: files }) =>
    add.mutate(
      { file: files[0], onProgress: setProgress },
      { onSuccess: onClose, onSettled: () => setProgress(null) },
    );

  return (
    <Modal
      isOpen
      onClose={add.isPending ? () => {} : onClose}
      title="Add Legislation"
      className="max-w-xl"
    >
      <form onSubmit={handleSubmit(submitForm)} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="file" className="form-label">
            File *
          </label>
          <input
            type="file"
            id="file"
            accept={ACCEPT}
            {...register("file", {
              validate: (files) => !!files?.length || "Choose a file",
            })}
            className="block w-full text-sm text-darkergray file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-lightgray file:text-darkergray hover:file:bg-mediumgray cursor-pointer"
            disabled={add.isPending}
          />
          {errors.file && (
            <p className="form-error-message">{errors.file.message}</p>
          )}
          <p className="text-sm text-darkgray">
            The official PDF, a Word file, text, or a regulator's page saved
            from the browser, up to 50 MB · or a zip of the legislation folder
            up to 2 GB, each law in it added on its own. A file already in the
            register is skipped.
          </p>
          <p className="text-sm text-darkgray">
            A stored law is never changed. Once it is read, open its details to
            register it: its jurisdiction and whether it is an Act, a Regulation
            or guidance. Only registered laws are searched and quoted.
          </p>
        </div>

        {progress !== null && (
          <div className="flex items-center gap-3">
            <CircularProgress value={progress} />
            <p className="text-sm text-darkgray">
              {progress < 100 ? "Uploading…" : "Uploaded, adding…"}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-mediumgray">
          <Button
            className="outline-button"
            onClick={onClose}
            disabled={add.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="black-button"
            loading={add.isPending}
          >
            Add
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ─── Register details ─────────────────────────────────────────────────────────
// Mounted only while open, so the form starts from the law's details instead of being reset.
const DetailsModal = ({ law, laws, jurisdictions, onClose }) => {
  const update = useUpdateLawMutation();
  const timezone = jurisdictions.find(
    (jurisdiction) => jurisdiction.code === law.jurisdiction,
  )?.timezone;
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: law.title || "",
      jurisdiction: law.jurisdiction || "",
      instrument: law.instrument || "",
      version: law.version || "",
      startsOn: dayInput(law.startsOn, timezone),
      nextCheckOn: dayInput(law.nextCheckOn, timezone),
      officialUrl: law.officialUrl || "",
      notes: law.notes || "",
      replaces: (law.replaces || []).map(String),
    },
  });

  const jurisdiction = watch("jurisdiction");
  // A law replaces only an earlier version of its own jurisdiction's law.
  const replaceable = laws
    .filter(
      (other) =>
        other._id !== law._id &&
        other.jurisdiction &&
        other.jurisdiction === jurisdiction,
    )
    .map((other) => ({
      value: other._id,
      label: `${other.title}${other.version ? ` · ${other.version}` : ""}`,
    }));
  const jurisdictionOptions = [
    { value: "", label: "Not registered" },
    ...jurisdictions.map(jurisdictionOption),
  ];
  const pick = (options, value) =>
    options.find((option) => option.value === value) || options[0];

  const submitForm = (values) =>
    update.mutate(
      {
        id: law._id,
        ...values,
        jurisdiction: values.jurisdiction || null,
        instrument: values.instrument || null,
        startsOn: values.startsOn || null,
        nextCheckOn: values.nextCheckOn || null,
        replaces: values.jurisdiction ? values.replaces : [],
      },
      { onSuccess: onClose },
    );

  return (
    <Modal
      isOpen
      onClose={update.isPending ? () => {} : onClose}
      title="Register Details"
      className="max-w-2xl"
    >
      <form onSubmit={handleSubmit(submitForm)} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="title" className="form-label">
            Title *
          </label>
          <input
            id="title"
            {...register("title", {
              validate: (value) => !!value.trim() || "Title is required",
            })}
            className={inputClass(errors.title)}
          />
          {errors.title && (
            <p className="form-error-message">{errors.title.message}</p>
          )}
          <p className="text-xs text-darkgray">{law.file?.name}</p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="form-label">Jurisdiction</label>
            <Controller
              name="jurisdiction"
              control={control}
              render={({ field }) => (
                <SearchSelect
                  value={pick(jurisdictionOptions, field.value)}
                  onChange={(option) => field.onChange(option?.value || "")}
                  options={jurisdictionOptions}
                />
              )}
            />
            <p className="text-xs text-darkgray">
              A country's law applies in every state under it.
            </p>
          </div>
          <div className="space-y-2">
            <label className="form-label">Type</label>
            <Controller
              name="instrument"
              control={control}
              render={({ field }) => (
                <SearchSelect
                  value={pick(INSTRUMENT_OPTIONS, field.value)}
                  onChange={(option) => field.onChange(option?.value || "")}
                  options={INSTRUMENT_OPTIONS}
                  searchable={false}
                />
              )}
            />
            <p className="text-xs text-darkgray">
              A rule must quote an Act or Regulation; guidance only alongside
              one.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor="version" className="form-label">
              Version
            </label>
            <input
              id="version"
              {...register("version")}
              className={inputClass()}
              placeholder="e.g. the date the PDF says it is current from"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="startsOn" className="form-label">
              Starts on
            </label>
            <input
              id="startsOn"
              type="date"
              {...register("startsOn")}
              className={inputClass()}
            />
            <p className="text-xs text-darkgray">
              Leave empty when it is already in force.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="form-label">Replaces</label>
          <Controller
            name="replaces"
            control={control}
            render={({ field }) => (
              <SearchSelect
                multiple
                value={replaceable.filter((option) =>
                  field.value?.includes(option.value),
                )}
                onChange={(options) =>
                  field.onChange((options || []).map((option) => option.value))
                }
                options={replaceable}
                placeholder={
                  jurisdiction
                    ? "Earlier versions of this law"
                    : "Register a jurisdiction first"
                }
                disabled={!jurisdiction}
              />
            )}
          />
          <p className="text-xs text-darkgray">
            From its start date the earlier version is replaced: Pilot stops
            finding it, and every rule quoting it is listed to check again.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label htmlFor="officialUrl" className="form-label">
              Official link
            </label>
            <input
              id="officialUrl"
              {...register("officialUrl")}
              className={inputClass()}
              placeholder="https://"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="nextCheckOn" className="form-label">
              Next check
            </label>
            <input
              id="nextCheckOn"
              type="date"
              {...register("nextCheckOn")}
              className={inputClass()}
            />
            <p className="text-xs text-darkgray">
              When to look for a newer version.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="notes" className="form-label">
            Notes
          </label>
          <textarea
            id="notes"
            rows={3}
            {...register("notes")}
            className={inputClass()}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-mediumgray">
          <Button
            className="outline-button"
            onClick={onClose}
            disabled={update.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="black-button"
            loading={update.isPending}
          >
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ─── What was read from the file ──────────────────────────────────────────────
const ReadTextModal = ({ id, onClose }) => {
  const { data, isPending } = useGetLawQuery(id);
  return (
    <Modal
      isOpen={!!id}
      onClose={onClose}
      title={data?.title || "Law"}
      className="max-w-3xl"
    >
      {isPending ? (
        <p className="text-center text-sm text-darkgray">Loading…</p>
      ) : data?.text ? (
        <>
          <p className="text-sm text-darkgray">
            The stored text rules quote and Pilot searches, in {data.sections}{" "}
            sections.
          </p>
          <pre className="whitespace-pre-wrap break-words rounded bg-lightgray p-4 text-xs text-darkergray">
            {data.text}
          </pre>
        </>
      ) : (
        <p className="text-center text-sm text-darkgray">
          {data?.status === "processing"
            ? "Still being read."
            : "Nothing has been read from this file."}
        </p>
      )}
    </Modal>
  );
};

const DeleteModal = ({ law, onClose }) => {
  const remove = useDeleteLawMutation();
  return (
    <Modal
      isOpen={!!law}
      onClose={onClose}
      title="Delete Law"
      className="max-w-sm"
    >
      <p className="text-sm text-darkergray">
        Delete <strong>{law?.title}</strong>? Pilot stops finding it straight
        away, and the stored file is deleted.
      </p>
      <div className="flex justify-end gap-2 pt-4">
        <Button className="outline-button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="black-button"
          loading={remove.isPending}
          onClick={() => remove.mutate(law._id, { onSuccess: onClose })}
        >
          Delete
        </Button>
      </div>
    </Modal>
  );
};

// ─── Try a question ───────────────────────────────────────────────────────────
const EVERY_JURISDICTION = { value: "", label: "Every jurisdiction" };

const SearchPanel = ({ jurisdictions }) => {
  const search = useSearchLegislationMutation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: { question: "", jurisdiction: EVERY_JURISDICTION },
  });

  const submitForm = ({ question, jurisdiction }) =>
    search.mutate({
      question: question.trim(),
      jurisdiction: jurisdiction.value,
    });

  return (
    <div className="mb-6 rounded border border-mediumgray p-4">
      <h5 className="mb-1">Try a question</h5>
      <p className="mb-3 text-sm text-darkgray">
        Ask the way an agent would, to see the sections Pilot quotes. Pick a
        jurisdiction to see what a property there gets: its own law and its
        country's.
      </p>
      <form onSubmit={handleSubmit(submitForm)} className="space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            {...register("question", {
              validate: (value) => !!value.trim() || "Type a question",
            })}
            className={`${inputClass(errors.question)} flex-1`}
            placeholder="e.g. When does cooling off end on a contract for sale?"
          />
          <div className="w-64">
            <Controller
              name="jurisdiction"
              control={control}
              render={({ field }) => (
                <SearchSelect
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    EVERY_JURISDICTION,
                    ...jurisdictions.map(jurisdictionOption),
                  ]}
                />
              )}
            />
          </div>
          <Button
            type="submit"
            className="black-button"
            loading={search.isPending}
          >
            Search
          </Button>
        </div>
        {errors.question && (
          <p className="form-error-message">{errors.question.message}</p>
        )}
      </form>

      {search.data && (
        <div className="mt-4 space-y-3">
          {search.data.length === 0 && (
            <p className="text-sm text-darkgray">
              Nothing close enough. Pilot would say the law isn't in the
              register yet.
            </p>
          )}
          {search.data.map((result, index) => (
            <div key={index} className="rounded bg-lightgray p-3">
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-darkgray">
                <span className="font-semibold text-darkergray">
                  {result.law}
                </span>
                <span>· {result.jurisdiction}</span>
                {result.section && <span>› {result.section}</span>}
                {result.where && <span>· {result.where}</span>}
                {result.startsOn && new Date(result.startsOn) > new Date() && (
                  <span className="rounded bg-blue-100 px-1.5 text-blue-800">
                    Starts {formatDate(result.startsOn)}
                  </span>
                )}
                {result.file?.link && (
                  <a
                    href={result.file.link}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    File
                  </a>
                )}
                <span className="ml-auto">
                  {Math.round(result.score * 100)}% match
                </span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-darkergray">
                {result.text.length > 600
                  ? `${result.text.slice(0, 600)}…`
                  : result.text}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const pageSize = 20;

const LegislationRegister = () => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [readingId, setReadingId] = useState(null);

  const { data, isPending } = useGetLegislationQuery();
  const { data: jurisdictions } = useGetJurisdictionsQuery();
  const readAgain = useReadLawAgainMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "title",
        header: "Law",
        isFilter: true,
        minWidth: "260px",
        cell: (row) => (
          <div>
            <p className="font-medium text-darkergray">{row.title}</p>
            <p className="text-xs text-darkgray">
              {row.file?.name} · {sizeOf(row.file?.size || 0)}
            </p>
            {row.version && (
              <p className="text-xs text-darkgray">{row.version}</p>
            )}
          </div>
        ),
      },
      {
        accessorKey: "jurisdiction",
        header: "Jurisdiction",
        isFilter: true,
        cell: (row) =>
          row.registered ? (
            row.jurisdiction
          ) : (
            <span className="rounded bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800">
              Not registered
            </span>
          ),
      },
      {
        accessorKey: "instrument",
        header: "Type",
        isFilter: true,
        cell: (row) => INSTRUMENT_LABEL[row.instrument] || "—",
      },
      {
        accessorKey: "standing",
        header: "Standing",
        isFilter: true,
        minWidth: "170px",
        cell: (row) => (
          <div>
            <span
              className={`rounded px-2 py-0.5 text-xs ${STANDING_STYLE[row.standing] || ""}`}
            >
              {STANDING_LABEL[row.standing] || row.standing}
            </span>
            {row.standing === "upcoming" && row.startsOn && (
              <p className="mt-1 text-xs text-darkgray">
                Starts {formatDate(row.startsOn)}
              </p>
            )}
            {row.replacedBy && (
              <p className="mt-1 text-xs text-darkgray">
                {row.standing === "replaced" ? "By" : "To be replaced by"}{" "}
                {row.replacedBy.title}
                {row.standing !== "replaced" &&
                  row.replacedBy.on &&
                  ` on ${formatDate(row.replacedBy.on)}`}
              </p>
            )}
          </div>
        ),
      },
      {
        accessorKey: "status",
        header: "Read",
        isFilter: true,
        minWidth: "130px",
        cell: (row) => (
          <div>
            <span
              className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[row.status] || ""}`}
            >
              {STATUS_LABEL[row.status] || row.status}
            </span>
            {row.status === "ready" && (
              <p className="mt-1 text-xs text-darkgray">
                {row.sections} sections
              </p>
            )}
            {row.status === "failed" && row.error && (
              <p className="form-error-message">{row.error}</p>
            )}
          </div>
        ),
      },
      {
        accessorKey: "nextCheckOn",
        header: "Next check",
        isFilter: false,
        cell: (row) =>
          row.nextCheckOn ? (
            <div className="text-xs">
              <p>{formatDate(row.nextCheckOn)}</p>
              {row.checkDue && (
                <span className="rounded bg-red-100 px-1.5 text-red-800">
                  Due
                </span>
              )}
            </div>
          ) : (
            "—"
          ),
      },
      {
        accessorKey: "quotedBy",
        header: "Rules quoting it",
        isFilter: false,
        cell: (row) => row.quotedBy || "—",
      },
      {
        accessorKey: "createdAt",
        header: "Added",
        isFilter: false,
        minWidth: "150px",
        cell: (row) => (
          <p className="text-xs">{formatDateTime(row.createdAt)}</p>
        ),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        cell: (row) => (
          <div className="flex items-center gap-3">
            <ActionIcon
              label="The stored text"
              onClick={() => setReadingId(row._id)}
            >
              <FiFileText size={16} />
            </ActionIcon>
            <ActionIcon
              label="Register details"
              onClick={() => setEditing(row)}
            >
              <FiEdit2 size={16} />
            </ActionIcon>
            {row.officialUrl && (
              <ActionIcon
                label="Official page"
                onClick={() =>
                  window.open(row.officialUrl, "_blank", "noopener")
                }
              >
                <FiExternalLink size={16} />
              </ActionIcon>
            )}
            <ActionIcon
              label={
                row.status === "processing"
                  ? "Being read now"
                  : "Read the file again"
              }
              disabled={row.status === "processing"}
              onClick={() => readAgain.mutate(row._id)}
            >
              <FiRefreshCw size={16} />
            </ActionIcon>
            <ActionIcon
              label={row.quotedBy ? "Rules quote it, so it stays" : "Delete"}
              disabled={!!row.quotedBy}
              onClick={() => setDeleting(row)}
            >
              <FiTrash2 size={16} />
            </ActionIcon>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const AddButton = () => (
    <Button
      className="black-button flex items-center gap-2"
      onClick={() => setIsAddOpen(true)}
    >
      <FaPlus size={14} />
      Add Legislation
    </Button>
  );

  if (isPending) return <PageLoader />;

  return (
    <>
      {isAddOpen && <AddModal onClose={() => setIsAddOpen(false)} />}
      {editing && (
        <DetailsModal
          law={editing}
          laws={data}
          jurisdictions={jurisdictions}
          onClose={() => setEditing(null)}
        />
      )}
      <DeleteModal law={deleting} onClose={() => setDeleting(null)} />
      <ReadTextModal id={readingId} onClose={() => setReadingId(null)} />

      <div className="container mx-auto">
        <p className="mb-4 text-sm text-darkgray">
          The laws Beleef answers from: Acts, Regulations and regulator
          guidance, stored as published. Pilot answers a law question only from
          registered laws here, and every jurisdiction rule quotes one word for
          word.
        </p>
        <SearchPanel jurisdictions={jurisdictions} />
        <Table
          title="Legislation Register"
          columns={columns}
          data={data}
          pageSize={pageSize}
          ButtonCmp={AddButton}
          emptyText="No laws yet."
        />
      </div>
    </>
  );
};

export default LegislationRegister;
